import { CadstromObjectType, isCadstromUidOfType, looksLikeCadstromUid } from '@cadstrom/cadstrom-shared';

/**
 * Organization ids come from Auth0 and share the `org_` prefix without meeting a
 * Cadstrom UID's 32-character suffix requirement, so they need their own shape check.
 */
const ORGANIZATION_ID_PATTERN = /^org_[a-zA-Z0-9]{8,}$/;

/**
 * Reports whether a value is shaped like an organization id.
 *
 * @param value - The value to test.
 * @returns `true` when `value` is an organization id; `false` for any other owner
 *          id, including a user id.
 */
export function isOrganizationId(value: string): boolean {
	return ORGANIZATION_ID_PATTERN.test(value);
}

/** UIDs enabled globally, with package navigation still capability-gated. */
export function supportedCadstromUidTypes(canViewPackages: boolean): CadstromObjectType[] {
	return [
		CadstromObjectType.AgentChatConversation,
		CadstromObjectType.Organization,
		CadstromObjectType.Project,
		CadstromObjectType.Build,
		...(canViewPackages ? [CadstromObjectType.PackageStorePackage] : []),
	];
}

/**
 * Resolves which supported object type a pasted command-bar value corresponds to.
 *
 * @param value Raw command-bar input.
 * @param supportedTypes The object types the command bar can render results for.
 * @returns The matching object type, or `null` if `value` is not a recognized id
 *          or its type is not in `supportedTypes`.
 */
export function resolveCadstromUidType(value: string, supportedTypes: CadstromObjectType[]): CadstromObjectType | null {
	if (supportedTypes.includes(CadstromObjectType.Organization) && isOrganizationId(value)) {
		return CadstromObjectType.Organization;
	}

	if (!looksLikeCadstromUid(value)) {
		return null;
	}

	return supportedTypes.find((type) => isCadstromUidOfType(value, type)) ?? null;
}
